'use client';

import React, { createContext, useContext, useCallback } from 'react';
import {
  useUserBusinesses,
  useActiveBusiness,
  useSetActiveBusiness,
  useCreateBusiness,
  type Business,
} from '@/hooks/react-query/business/use-business';

interface BusinessContextType {
  // Data
  businesses: Business[] | undefined;
  activeBusiness: Business | null | undefined;
  
  // Loading states
  isLoadingBusinesses: boolean;
  isLoadingActive: boolean;
  isSwitching: boolean;
  isCreating: boolean;
  
  // Actions
  switchBusiness: (businessId: string) => Promise<void>;
  createBusiness: (name: string, setActive?: boolean) => Promise<Business>;
  
  // Computed
  hasBusinesses: boolean;
}

const BusinessContext = createContext<BusinessContextType | undefined>(undefined);

export function BusinessProvider({ children }: { children: React.ReactNode }) {
  const { data: businesses, isLoading: isLoadingBusinesses } = useUserBusinesses();
  const { data: activeBusiness, isLoading: isLoadingActive } = useActiveBusiness();
  const setActiveMutation = useSetActiveBusiness();
  const createMutation = useCreateBusiness();

  const switchBusiness = useCallback(async (businessId: string) => {
    await setActiveMutation.mutateAsync(businessId);
  }, [setActiveMutation]);

  const createBusiness = useCallback(async (name: string, setActive = true) => {
    return await createMutation.mutateAsync({ name, setActive });
  }, [createMutation]);

  const value: BusinessContextType = {
    businesses,
    activeBusiness,
    isLoadingBusinesses,
    isLoadingActive,
    isSwitching: setActiveMutation.isPending,
    isCreating: createMutation.isPending,
    switchBusiness,
    createBusiness,
    hasBusinesses: !!(businesses && businesses.length > 0),
  };

  return (
    <BusinessContext.Provider value={value}>
      {children}
    </BusinessContext.Provider>
  );
}

export function useBusiness() {
  const context = useContext(BusinessContext);
  if (context === undefined) {
    throw new Error('useBusiness must be used within a BusinessProvider');
  }
  return context;
}

// Hook to get the active business ID (commonly needed)
export function useActiveBusinessId(): string | undefined {
  const { activeBusiness } = useBusiness();
  return activeBusiness?.id;
}
